import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';

import {
  InventoryItem,
  ItemHistoryView,
} from '../../core/models/inventory.models';
import { InventoryService } from '../../core/services/inventory.service';
import { SettingsService } from '../../core/services/settings.service';
import { currentTimestamp, formatDate } from '../../core/utils/format.util';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-item-history',
  standalone: true,
  imports: [FormsModule, RouterLink],
  templateUrl: './item-history.component.html',
  styleUrl: './item-history.component.css',
})
export class ItemHistoryComponent implements OnInit {
  private readonly inventory = inject(InventoryService);
  private readonly settings = inject(SettingsService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly timestamp = currentTimestamp;
  readonly formatDate = formatDate;
  readonly catalog = this.inventory.allItems;

  readonly selectedItemId = signal('');
  readonly itemSearch = signal('');
  readonly startDate = signal('');
  readonly endDate = signal('');
  readonly loading = signal(false);
  readonly error = signal('');
  readonly history = signal<ItemHistoryView | null>(null);

  readonly filteredCatalog = computed(() => {
    const search = this.itemSearch().trim().toLowerCase();
    const items = this.catalog().filter(
      (item) => item.status !== 'discontinued',
    );
    if (!search) {
      return items.slice(0, 80);
    }
    return items
      .filter(
        (item) =>
          item.name.toLowerCase().includes(search) ||
          item.code.toLowerCase().includes(search) ||
          item.category.toLowerCase().includes(search),
      )
      .slice(0, 80);
  });

  readonly selectedItem = computed(
    () =>
      this.catalog().find((item) => item.id === this.selectedItemId()) ?? null,
  );

  ngOnInit(): void {
    if (!environment.useMockData) {
      this.settings.refreshFromApi();
      this.inventory.refreshFromApi();
    }

    this.route.paramMap.subscribe((params) => {
      const id = params.get('itemId') ?? '';
      if (id && id !== this.selectedItemId()) {
        this.selectedItemId.set(id);
        this.loadHistory();
      } else if (!id) {
        this.selectedItemId.set('');
        this.history.set(null);
        this.error.set('');
      }
    });
  }

  onSelectItem(itemId: string): void {
    this.selectedItemId.set(itemId);
    if (!itemId) {
      this.history.set(null);
      void this.router.navigate(['/item-history']);
      return;
    }
    void this.router.navigate(['/item-history', itemId]);
    this.loadHistory();
  }

  pickCatalogItem(item: InventoryItem): void {
    this.itemSearch.set('');
    this.onSelectItem(item.id);
  }

  loadHistory(): void {
    const id = this.selectedItemId();
    if (!id) {
      this.error.set('Select an item to see where it is and where it went.');
      this.history.set(null);
      return;
    }

    this.loading.set(true);
    this.error.set('');
    this.inventory
      .fetchItemHistory(id, {
        startDate: this.startDate() || undefined,
        endDate: this.endDate() || undefined,
      })
      .subscribe((result) => {
        this.loading.set(false);
        if (!result.success || !result.data) {
          this.history.set(null);
          this.error.set(
            result.message ?? 'Could not load this item’s history.',
          );
          return;
        }
        this.history.set(result.data);
      });
  }

  formatWhen(value: string): string {
    if (!value) {
      return '—';
    }
    const iso = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
      return formatDate(iso);
    }
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) {
      return iso.slice(0, 10);
    }
    return new Intl.DateTimeFormat('en-ZA', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(parsed);
  }

  directionClass(direction: string): string {
    if (direction === 'in') return 'in';
    if (direction === 'hold') return 'hold';
    if (direction === 'move') return 'move';
    return 'out';
  }

  placeLabel(event: {
    fromLocation: string;
    toLocation: string;
    destination: string;
    branchName: string;
  }): string {
    if (event.destination) {
      return event.destination;
    }
    if (event.fromLocation && event.toLocation) {
      return `${event.fromLocation} → ${event.toLocation}`;
    }
    return event.toLocation || event.fromLocation || event.branchName || '—';
  }
}
